import { motion, AnimatePresence } from 'framer-motion';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import styles from './Statistics.module.sass';

interface StatisticsProps {
  highlights: any;
  expandedCard: string | null;
  onCardExpand: (title: string) => void;
  lastUpdate: Date;
}

const Statistics: React.FC<StatisticsProps> = ({
  highlights,
  expandedCard,
  onCardExpand,
  lastUpdate
}) => {
  if (!highlights) {
    return null;
  }

  // Format number with commas and 2 decimal places
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const statsData = [
    {
      title: 'Total Portfolio Value',
      value: formatCurrency(Number(highlights?.total_portfolio_value || 0)),
      details: 'Total value across all assets and networks',
      identifiers: [],
      color: '#73F6FF'
    },
    {
      title: 'Top Holding',
      value: formatCurrency(Number(highlights?.top_holding_dollar_value || 0)),
      details: highlights?.top_holding 
        ? `${highlights.top_holding} - Value: ${formatCurrency(Number(highlights.top_holding_dollar_value || 0))}`
        : 'No holdings',
      identifiers: highlights?.top_holding_logo 
        ? [<div className={styles.badgeImage} style={{
            width: '42px',
            height: '42px',
            borderRadius: '8px',
            background: 'rgba(115, 246, 255, 0.1)',
            border: '1px solid rgba(115, 246, 255, 0.3)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            top: '12px',
            right: '12px'
          }}>
            <img 
              src={highlights.top_holding_logo} 
              alt="Top holding logo"
              style={{
                width: '80%',
                height: '80%',
                objectFit: 'contain'
              }}
            />
          </div>] 
        : [],
      color: '#73F6FF'
    },
    {
      title: 'Asset Count',
      value: highlights?.asset_count ?? 0,
      details: highlights?.top_5_assets?.length 
        ? `Top assets: ${highlights.top_5_assets.join(', ')}`
        : 'No assets',
      identifiers: highlights?.top_5_assets ?? [],
      color: '#73F6FF'
    },
    {
      title: 'Networks',
      value: highlights?.network_count ?? 0,
      details: highlights?.networks?.length 
        ? `Active on: ${highlights.networks.join(', ')}`
        : 'No networks',
      identifiers: highlights?.networks ?? [],
      color: '#73F6FF'
    }
  ];

  return (
    <div>
      <motion.div 
        style={{
          background: 'linear-gradient(135deg, #1E1E28, #131318)',
          borderRadius: '16px',
          border: '1px solid rgba(59, 59, 65, 0.5)',
          padding: '20px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background grid pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'linear-gradient(rgba(115, 246, 255, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(115, 246, 255, 0.02) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
          opacity: 0.5
        }} />

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          padding: '0 8px',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{
              color: '#E6E6E6',
              fontSize: '18px',
              fontWeight: 600,
              margin: 0,
              letterSpacing: '0.5px'
            }}>Portfolio Overview</h2>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#73F6FF',
              boxShadow: '0 0 10px #73F6FF',
              animation: 'pulse 2s infinite'
            }} />
          </div>

          <motion.span
            style={{
              color: '#73F6FF',
              fontSize: '12px',
              background: 'rgba(115, 246, 255, 0.1)',
              padding: '4px 12px',
              borderRadius: '12px',
              border: '1px solid rgba(115, 246, 255, 0.2)',
              boxShadow: '0 0 10px rgba(115, 246, 255, 0.3)',
              fontWeight: 500
            }}
            animate={{ opacity: [1, 0.6, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            Last updated: {lastUpdate.toLocaleTimeString()}
          </motion.span>
        </div>

        <div className={styles.container} style={{ gap: '12px' }}>
          <AnimatePresence>
            {statsData.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  scale: expandedCard === stat.title ? 1.02 : 1,
                  zIndex: expandedCard === stat.title ? 2 : 1
                }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={styles.statsCard}
                style={{
                  backgroundColor: '#1B1B24',
                  border: expandedCard === stat.title 
                    ? `1px solid ${stat.color}` 
                    : '1px solid #3B3B41',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  borderRadius: '10px',
                  boxShadow: expandedCard === stat.title 
                    ? `0 4px 16px rgba(115, 246, 255, 0.15)` 
                    : '0 2px 8px rgba(0, 0, 0, 0.15)',
                  padding: '12px',
                  minHeight: '90px',
                  height: '100%'
                }}
                onClick={() => onCardExpand(stat.title)}
                whileHover={{
                  border: `1px solid ${stat.color}`,
                  boxShadow: `0 4px 20px rgba(115, 246, 255, 0.1)`
                }}
              >
                {/* Card content without the icon container */}
                <div className={styles.statsCardContent} style={{
                  width: '100%'
                }}>
                  <div className={styles.statsCardTitle} style={{
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                    marginBottom: '6px',
                    color: '#B3B3B3'
                  }}>
                    {stat.title}
                  </div>
                  <div className={styles.statsCardValueContainer}>
                    <div className={styles.statsCardValue} style={{
                      color: stat.color,
                      textShadow: `0 0 10px rgba(115, 246, 255, 0.3)`,
                      fontSize: '20px',
                      fontWeight: 600
                    }}>
                      {stat.value}
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedCard === stat.title && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'rgba(115, 246, 255, 0.05)',
                          padding: '8px',
                          borderTop: '1px solid rgba(115, 246, 255, 0.1)'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          color: '#D1D1D1',
                          fontSize: '12px',
                          fontWeight: 400
                        }}>
                          <InfoOutlinedIcon style={{ fontSize: '16px', color: '#73F6FF' }} />
                          {stat.details}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                
                {stat.identifiers && stat.identifiers.length > 0 && (
                  <div className={styles.identifiers} style={{
                    marginTop: '12px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px'
                  }}>
                    {stat.identifiers.map((identifier, idx) => (
                      typeof identifier === 'string' ? (
                        <span 
                          key={`${identifier}-${idx}`} 
                          className={styles.identifier}
                          style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            color: '#73F6FF',
                            backgroundColor: 'rgba(115, 246, 255, 0.1)',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            border: '1px solid rgba(115, 246, 255, 0.2)'
                          }}
                        >
                          {identifier}
                        </span>
                      ) : (
                        identifier
                      )
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.div>

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Statistics;